<template>
    <div class="AieManuscriptCategorization">
        <div class="AieManuscriptCategorization__form">
            <AcceptanceReviewContent
                :field-name="'Endorsement Review - Additional Insured Endorsement'"
                :actual-value="'Manuscript'"
                :required-value="actionDetails.categories.join(',')"
                :is-global-type="false"
                :read-only="readOnly"
                :outcome="recordedOutcome"
                @accept="resolve(true)"
                @reject="resolve(false)"
            />
        </div>
        <div class="AieManuscriptCategorization__documents">
            <LogoLoader v-if="loading" />
            <DocumentsViewer v-if="!loading" :documents="docsStatus.list" />
        </div>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { LogoLoader } from '@evidentid/dashboard-commons/components/LogoLoader';
    import { typeNarrowing } from '@evidentid/dashboard-commons/utils/typeNarrowing';
    import {
        ActionType,
        ActionReview, AcceptanceActionResolveInput,
    } from '@evidentid/tprm-portal-lib/models/entity-actions-review';
    import {
        AieManuscriptActionDetails,
    } from '@evidentid/tprm-portal-lib/models/entity-actions-review/actions/aie-manuscript-categorization/AieManuscriptActionDetails';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import { DashboardState, useDashboardStore } from '@/modules/dashboard/vuex/dashboard';
    import AcceptanceReviewContent
        from '@/modules/entity-actions-review/components/AcceptanceReviewContent/AcceptanceReviewContent.vue';
    import DocumentsViewer from '@/modules/entity-actions-review/components/DocumentsViewer/DocumentsViewer.vue';
    import { WRONG_ACTION_DETAILS_TYPE_MESSAGE } from '@/modules/entity-actions-review/constants';
    import { CoiDocumentStatus, useEntityActionsReviewStore } from '@/modules/entity-actions-review/store';

    export default defineComponent({
        name: 'AieManuscriptCategorization',
        components: {
            AcceptanceReviewContent,
            DocumentsViewer,
            LogoLoader,
        },
        props: {
            actionReview: {
                type: Object as PropType<ActionReview>,
                default: () => ({} as ActionReview),
            },
            readOnly: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'resolve' ],
        computed: {
            recordedOutcome(): 'accepted' | 'rejected' | null {
                return this.actionReview.resolution?.outcome ?? null;
            },
            rpName(): string {
                return this.$rp.current!;
            },
            actionDetails(): AieManuscriptActionDetails {
                return typeNarrowing(
                    this.actionReview.action,
                    this.actionReview.action.$action === ActionType.aieManuscriptCategorization,
                    WRONG_ACTION_DETAILS_TYPE_MESSAGE,
                );
            },
            docsStatus(): CoiDocumentStatus {
                if (useEntityActionsReviewStore().rprDocuments[this.rpName] &&
                    useEntityActionsReviewStore().rprDocuments[this.rpName][this.actionReview.id]) {
                    return useEntityActionsReviewStore().rprDocuments[this.rpName][this.actionReview.id];
                } else {
                    return { status: OperationStatus.uninitialized, list: [] };
                }
            },
            requirementDefinitions(): DashboardState['tprmRequirementsDefinitions'] {
                return (useDashboardStore().$state as DashboardState).tprmRequirementsDefinitions || {};
            },
            loading(): boolean {
                return this.docsStatus.status === OperationStatus.loading;
            },
        },
        watch: {
            actionReview: {
                immediate: true,
                handler() {
                    if (this.docsStatus.status === OperationStatus.uninitialized &&
                        this.actionDetails.verificationId
                    ) {
                        useEntityActionsReviewStore().loadRprDocuments({
                            rpName: this.rpName,
                            actionId: this.actionReview.id,
                            documentsFor: [ {
                                verificationId: this.actionDetails.verificationId,
                                requirementType: this.actionDetails.coverageType,
                            } ],
                        });
                    }
                },
            },
        },
        methods: {
            resolve(accepted: boolean): void {
                const data: AcceptanceActionResolveInput = {
                    $action: ActionType.aieManuscriptCategorization,
                    accepted,
                };
                this.$emit('resolve', data);
            },
        },
    });
</script>
